import React from 'react';
import PropTypes from 'prop-types';
import onClickOutside from '@adsk/react-onclickoutside';
import MenuItemIcon from '@adsk/bim360-matrix-react-menu-item-icon';

import { callOrReturn } from './utils';

import './_ContextMenu.scss';

const defaultSvgProps = {
  height: '20px',
  width: '20px'
};

/**
 * ContextMenu for MatrixTable
 */
class ContextMenu extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      rowData: null,
      rowIndex: null,
      options: null
    };
    this._isOpen = false;

    this._setMenuRef = this._setMenuRef.bind(this);
    this._setArrowRef = this._setArrowRef.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleScroll = this._handleScroll.bind(this);
    this._handleResize = this._handleResize.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
  }

  open = ({ event, rowData, rowIndex }) => {
    const { actionOptions } = this.props;
    const options = callOrReturn(actionOptions, { rowData, rowIndex });
    if (!Array.isArray(options) || options.length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    const { clientX, clientY } = event;
    this.setState({ rowData, rowIndex, options }, () => {
      this.updatePosition({ clientX, clientY });
    });
  };

  updatePosition({ clientX, clientY }) {
    if (!this.menu) return;

    const menuWidth = this.menu.offsetWidth;
    const menuHeight = this.menu.offsetHeight;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let left, top, arrowOffset;

    if (Math.min(clientX, windowWidth - clientX) >= menuWidth / 2) {
      left = clientX - menuWidth / 2;
      arrowOffset = 0;
    } else if (clientX < menuWidth / 2) {
      left = 0;
      arrowOffset = clientX - menuWidth / 2;
    } else {
      left = windowWidth - menuWidth;
      arrowOffset = menuWidth / 2 - (windowWidth - clientX);
    }

    if (windowHeight - clientY > menuHeight || clientY < menuHeight) {
      top = clientY;
      this.menu.className = 'ContextMenu';
    } else {
      top = clientY - menuHeight;
      this.menu.className = 'ContextMenu ContextMenu--top';
    }

    this.menu.style.left = `${left}px`;
    this.menu.style.top = `${top}px`;
    this.menu.style.visibility = 'visible';
    this.arrow.style.marginLeft = `${arrowOffset}px`;
    this._isOpen = true;
  }

  close = () => {
    if (!this._isOpen || !this.menu) return;

    this.menu.style.visibility = 'hidden';
    this._isOpen = false;
  };

  handleClickOutside() {
    this.close();
  }

  render() {
    const { options } = this.state;

    return (
      <ul ref={this._setMenuRef} className="ContextMenu">
        {options && <div ref={this._setArrowRef} className="ContextMenu__arrow" />}
        {options &&
          options.map(({ key, label, disabled, ...svgProps }) => (
            <MenuItemIcon
              key={key}
              eventKey={key}
              disabled={disabled}
              onClick={this._handleClick}
              svgProps={{ ...defaultSvgProps, ...svgProps }}
            >
              {label}
            </MenuItemIcon>
          ))}
      </ul>
    );
  }

  componentDidMount() {
    window.addEventListener('scroll', this._handleScroll, true);
    window.addEventListener('resize', this._handleResize);
    window.addEventListener('keydown', this._handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this._handleScroll, true);
    window.removeEventListener('resize', this._handleResize);
    window.removeEventListener('keydown', this._handleKeyDown);
  }

  _setMenuRef(ref) {
    this.menu = ref;
  }

  _setArrowRef(ref) {
    this.arrow = ref;
  }

  _handleClick(key) {
    this.close();

    const { onRowAction } = this.props;
    const { rowData, rowIndex } = this.state;
    onRowAction && onRowAction({ key, rowData, rowIndex });
  }

  _handleScroll() {
    this.close();
  }

  _handleResize() {
    this.close();
  }

  _handleKeyDown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      this.close();
    }
  }
}

ContextMenu.propTypes = {
  actionOptions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.func]),
  rowData: PropTypes.object,
  rowIndex: PropTypes.number,
  onRowAction: PropTypes.func
};

export default onClickOutside(ContextMenu);
